import React, { useEffect, useState } from "react";
import { db } from "../../config/firebaseConfig";
import { ref, get } from "firebase/database";

interface PrivacyPolicySection {
  title: string;
  content: string;
  listItems?: string[];
}

interface PrivacyPolicyData {
  introduction: string;
  sections: PrivacyPolicySection[];
}

const PrivacyPolicySkeleton: React.FC = () => {
  return (
    <div className="p-4 md:px-[15vw] md:pt-[5vh] w-full flex flex-col font-normal text-sm space-y-5">
      <div>
        <div className="mt-[4%] h-12 w-3/4 bg-gray-200 animate-pulse rounded"></div>
      </div>
      {/* Introduction skeleton */}
      <div className="h-16 bg-gray-200 animate-pulse rounded"></div>

      {/* Sections skeleton */}
      <div className="space-y-6">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
          <div key={index} className="space-y-2">
            <div className="h-6 w-1/3 bg-gray-200 animate-pulse rounded"></div>
            <div className="h-16 bg-gray-200 animate-pulse rounded"></div>
            {/* List items skeleton */}
            {index === 1 || index === 2 ? (
              <div className="ml-4 space-y-2 mt-2">
                {[1, 2, 3, 4].map((item) => (
                  <div
                    key={item}
                    className="h-4 w-2/3 bg-gray-200 animate-pulse rounded"
                  ></div>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

const PrivacyPolicy: React.FC = () => {
  const [privacyData, setPrivacyData] = useState<PrivacyPolicyData>({
    introduction: "",
    sections: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const aboutElement = document.getElementById("topbartopscroll");
    if (aboutElement) {
      aboutElement.scrollIntoView({ behavior: "smooth" });
    }

    // Fetch privacy policy data from Firebase
    const fetchPrivacyData = async () => {
      try {
        const privacyRef = ref(db, "LayoutSettingData/PrivacyPolicy");
        const snapshot = await get(privacyRef);

        if (snapshot.exists()) {
          setPrivacyData(snapshot.val());
        } else {
          // If no data exists, use default content
          const defaultData: PrivacyPolicyData = {
            introduction:
              "At Afrocart, we are committed to protecting your personal information and ensuring your experience with us is safe and secure. This Privacy Policy outlines how we collect, use, and protect your personal information when you visit our website or use our services",
            sections: [
              {
                title: "Information We collect",
                content:
                  "We collect personal information that you provide to us directly when you create an account, place an order, sign up for our newsletter, or interact with our customer support. This information may include your name, email address, phone number, payment information, and delivery address. We also collect information automatically through cookies and similar technologies, such as your IP address, browsing behavior, and device information.",
              },
              {
                title: "Google Calendar Access",
                content:
                  "If you choose to connect your Google Calendar to Afrocart, we will request permission to:",
                listItems: [
                  "Add new events to your Google Calendar.",
                  "Edit existing events based on your requests.",
                  "Retrieve your calendar events to display or synchronize with our platform.",
                  "Update events to reflect changes in scheduling.",
                ],
              },
              {
                title: "How We Use Your Information",
                content: "We use the information we collect to:",
                listItems: [
                  "Process and fulfill your orders",
                  "Provide customer support",
                  "Improve and personalize your shopping experience",
                  "Send you updates, promotions, and relevant news (if you have opted in)",
                  "Analyze and improve our website and services",
                ],
              },
              {
                title: "How We Protect Your Information",
                content:
                  "We take the security of your personal information seriously. We use industry-standard security measures, including encryption and secure servers, to protect your data from unauthorized access, alteration, disclosure, or destruction.",
              },
              {
                title: "Sharing Your Information",
                content:
                  "We do not sell your personal information to third parties. We may share your information with trusted third-party service providers who assist us in operating our website, processing payments, and delivering your orders. These partners are required to maintain the confidentiality of your information and are prohibited from using it for any other purpose.",
              },
              {
                title: "Your Rights",
                content:
                  "You have the right to access, correct, or delete your personal information at any time. If you would like to update your information or request the deletion of your account, please contact our customer service team.",
              },
              {
                title: "Cookies And Tracking Technologies",
                content:
                  "Our website uses cookies to enhance your shopping experience and provide personalized content. You can choose to disable cookies through your browser settings, but this may affect your ability to use certain features of our website.",
              },
              {
                title: "Updating Our Privacy Policy",
                content:
                  "We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We will notify you of any significant changes by posting the updated policy on our website and updating the effective date at the top.",
              },
            ],
          };
          setPrivacyData(defaultData);
        }
      } catch (error) {
        console.error("Error fetching privacy policy data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPrivacyData();
  }, []);

  if (loading) {
    return <PrivacyPolicySkeleton />;
  }

  return (
    <div className="p-4 md:px-[15vw] md:pt-[5vh] w-full flex flex-col font-normal text-sm space-y-5 text-black">
      <div>
        {/* <p className="flex items-start text-[1.65vh] text-customGrayBase">
            Last modified: 10/22/2024
          </p> */}
        <h1 className="mt-[4%] font-helveticaBold text-[5.5vh] xxl:text-[7.5vh] leading-none text-black">
          Afrocart Privacy Policy
        </h1>
      </div>
      <p>{privacyData.introduction}</p>
      <ol className="decimal_ol space-y-4 px-4">
        {privacyData.sections.map((section, index) => (
          <li key={index} className="font-helveticaBold text-black">
            {section.title}
            <p className="font-helvetica text-black">{section.content}</p>
            {section.listItems && section.listItems.length > 0 && (
              <ul className="font-helvetica disc_li px-4">
                {section.listItems.map((item, itemIndex) => (
                  <li key={itemIndex}>{item}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};

export default PrivacyPolicy;
